import React, { FC, useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useOktaAuth } from '@okta/okta-react'
import * as yup from 'yup'
import { routes, getAuthClient } from 'config'
import {
  constants,
  processErrorMsg,
  removeFocusFromField,
  storage,
} from 'utils'
import { BaseLayout, Loader } from 'components'
import { MfaOtpForm } from './components'
import useStyles from './mfa-style'

const MFA: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location?.state as {
    loginPassword: string
  }
  const loginPassword = state?.loginPassword ?? ''
  const { oktaAuth } = useOktaAuth()
  const [transaction, setTransaction] = useState<any | {}>({})
  const [showLoader, setShowLoader] = useState(false)
  const [serviceError, setServiceError] = useState('')
  const [count, setCount] = useState(5)
  const { classes } = useStyles()

  useEffect(() => {
    let authClient = getAuthClient()

    if (authClient.tx.exists()) {
      setShowLoader(true)

      authClient.tx
        .resume()
        .then((response: any) => {
          storage.setUser(response?.user?.profile?.login)
          setShowLoader(false)
          setTransaction(response)
        })
        .catch((e: any) => processError(e, 'MFA', 'Resuming transaction'))
    } else {
      navigate(routes.login)
    }
  }, [navigate])

  useEffect(() => {
    if (
      transaction &&
      transaction.status === constants.OKTA_TRANSACTION_STATUS.MFA_REQUIRED
    ) {
      localStorage.setItem('COUNT', JSON.stringify(count))
      const factor = transaction.factors.find(
        (value: any) => value.factorType === 'sms',
      )
      factor
        .verify({
          rememberDevice: true,
        })
        .then(processSuccess)
        .catch(processError)
    } else if (
      transaction.status ===
        constants.OKTA_TRANSACTION_STATUS.PASSWORD_EXPIRED ||
      transaction.status === constants.OKTA_TRANSACTION_STATUS.PASSWORD_WARN
    ) {
      // password is expiring soon (PASSWORD_WARN) or password already expired, let password-expired page handle both cases
      navigate(routes.passwordExpired, {
        state: { loginPassword },
      })
    } else if (
      transaction.status === constants.OKTA_TRANSACTION_STATUS.LOCKED_OUT
    ) {
      setServiceError('E0000069')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction])

  const processSuccess = (response: any) => {
    setShowLoader(false)
    setTransaction(response)
  }
  const decreaseCount = () => {
    let localCount: any = localStorage.getItem('COUNT')
    if (JSON.parse(localCount) >= 2) {
      setCount(JSON.parse(localCount) - 1)

      setTimeout(() => {
        localStorage.setItem('COUNT', JSON.stringify(--localCount))
      }, 700)
    } else {
      localStorage.removeItem('COUNT')
    }
  }

  const processError = async (
    e: any,
    module: string,
    moduleDescription: string = '',
  ) => {
    //APT-1222: Fixing error cases when MFA code is outdated
    if (e.toString().includes('Invalid token provided')) {
      await oktaAuth.signOut({}).then(() => {
        localStorage.removeItem('okta-cache-storage')
        localStorage.removeItem('okta-original-uri-storage')
        localStorage.removeItem('okta-shared-transaction-storage')
      })
    }

    setShowLoader(false)
    setServiceError(processErrorMsg(e, module, moduleDescription))
  }

  const handleSubmit = (values: any, { setSubmitting }: any) => {
    removeFocusFromField()
    setSubmitting(true)
    setServiceError('')
    setShowLoader(true)
    switch (transaction.status) {
      case constants.OKTA_TRANSACTION_STATUS.MFA_REQUIRED:
        localStorage.setItem('COUNT', JSON.stringify(count))
        const factor = transaction.factors.find(
          (value: any) => value.factorType === 'sms',
        )
        factor
          .verify({
            rememberDevice: true,
          })
          .then(processSuccess)
          .catch(processError)
          .then(() => setSubmitting(false))
        break
      case constants.OKTA_TRANSACTION_STATUS.MFA_CHALLENGE:
        decreaseCount()
        transaction
          .verify({
            rememberDevice: true,
            passCode: values.verificationCode,
          })
          .then(processSuccess)
          .catch(processError)
          .then(() => setSubmitting(false))
        break
      case constants.OKTA_TRANSACTION_STATUS.MFA_ENROLL_ACTIVATE:
        decreaseCount()
        transaction
          .activate({
            passCode: values.verificationCode,
          })
          .then(processSuccess)
          .catch(processError)
          .then(() => setSubmitting(false))
        break
    }
  }

  const handleResend = (event: any) => {
    event.stopPropagation()
    event.preventDefault()
    try {
      transaction.resend('email')
    } catch (e) {
      processError(e, 'MFA', 'Resending MFA OTP code')
    }
  }

  if (transaction.sessionToken) {
    oktaAuth.signInWithRedirect({ sessionToken: transaction.sessionToken })

    return null
  }

  let formContent: any
  let validationSchema: any
  let initialValues: any
  switch (transaction.status) {
    case constants.OKTA_TRANSACTION_STATUS.MFA_CHALLENGE:
      initialValues = {
        verificationCode: '',
      }

      validationSchema = yup.object().shape({
        verificationCode: yup.string().required('Required').length(6),
      })

      formContent = (
        <MfaOtpForm
          initialValues={initialValues}
          validationSchema={validationSchema}
          handleSubmit={handleSubmit}
          serviceError={serviceError}
          handleResend={handleResend}
          transaction={transaction}
          count={count}
          setShowLoader={setShowLoader}
        />
      )
      break
    case constants.OKTA_TRANSACTION_STATUS.MFA_ENROLL_ACTIVATE:
      validationSchema = yup.object().shape({
        verificationCode: yup.string().required('Required').length(6),
      })
      initialValues = {
        verificationCode: '',
      }

      formContent = (
        <MfaOtpForm
          transaction={transaction}
          initialValues={initialValues}
          handleSubmit={handleSubmit}
          serviceError={serviceError}
          validationSchema={validationSchema}
          count={count}
          handleResend={handleResend}
          setShowLoader={setShowLoader}
        />
      )
      break
  }

  return (
    <BaseLayout boxLayoutClassName={classes.mfaContainer}>
      <Loader visible={showLoader} />
      {formContent}
    </BaseLayout>
  )
}

export default MFA
